// CustomerDetailScreen.js
// Shows Customer and Vehicle sections matching the desktop layout.
// Editable for Held inspections, read-only for completed.

/* eslint-disable react-hooks/exhaustive-deps */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const US_STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'];

export default function CustomerDetailScreen() {
  const { getClient } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { pkInspection, fkCustomer, fkVehicle, isReadOnly, historyState, section } = location.state || {};
  const showCustomer = !section || section === "customer";
  const showVehicle = !section || section === "vehicle";

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [custForm, setCustForm] = useState(null);
  const [vehForm, setVehForm] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const client = getClient();
      const [custRes, vehRes] = await Promise.all([
        client.get(`/api/customer/${fkCustomer}`),
        client.get(`/api/vehicle/${fkVehicle}`),
      ]);
      setCustForm({ ...custRes.data });
      setVehForm({ ...vehRes.data });
    } catch {
      setError('Could not load customer/vehicle.');
    } finally {
      setLoading(false);
    }
  }, [fkCustomer, fkVehicle, getClient]);

  useEffect(() => { load(); }, []);

  const updateCust = (field, value) => setCustForm(prev => ({ ...prev, [field]: value }));
  const updateVeh = (field, value) => setVehForm(prev => ({ ...prev, [field]: value }));

  const handleApply = async () => {
    setSaving(true);
    setError('');
    try {
      const client = getClient();
      await Promise.all([
        client.put(`/api/customer/${fkCustomer}`, custForm),
        client.put(`/api/vehicle/${fkVehicle}`, vehForm),
      ]);
      navigate(`/inspection/${pkInspection}`, { state: { pkInspection, historyState } });
    } catch {
      setError('Could not save.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div style={s.page}><div style={s.center}><div style={s.spinner} /></div></div>;

  return (
    <div style={s.page}>
      <div style={s.header}>
        <span style={s.headerTitle}>{section === 'vehicle' ? 'Vehicle' : 'Customer'}</span>
        {pkInspection && <span style={s.headerSub}>Inspection #{pkInspection}</span>}
      </div>

      {error && <div style={s.error}>{error}</div>}

      <div style={s.body}>
        {/* Customer Section */}
        {showCustomer && custForm && (
          <div style={s.section}>
            <div style={s.sectionTitle}>Customer</div>
            <div style={s.twoPanel}>
              {/* Left panel */}
              <div style={s.panel}>
                <div style={s.radioRow}>
                  <label style={s.radioLabel}><input type="radio" checked={custForm.customerType === 'I'} onChange={() => !isReadOnly && updateCust('customerType', 'I')} disabled={isReadOnly} /> Individual</label>
                  <label style={s.radioLabel}><input type="radio" checked={custForm.customerType === 'B'} onChange={() => !isReadOnly && updateCust('customerType', 'B')} disabled={isReadOnly} /> Company</label>
                </div>
                {custForm.customerType === 'I' ? (
                  <div style={s.fieldRow}>
                    <div style={s.field}>
                      <label style={s.label}>Last Name</label>
                      <input style={s.input} value={custForm.lastName || ''} onChange={e => updateCust('lastName', e.target.value)} disabled={isReadOnly} />
                    </div>
                    <div style={s.field}>
                      <label style={s.label}>First Name</label>
                      <input style={s.input} value={custForm.firstName || ''} onChange={e => updateCust('firstName', e.target.value)} disabled={isReadOnly} />
                    </div>
                  </div>
                ) : (
                  <div style={s.field}>
                    <label style={s.label}>Business Name</label>
                    <input style={s.input} value={custForm.businessName || ''} onChange={e => updateCust('businessName', e.target.value)} disabled={isReadOnly} />
                  </div>
                )}
                <div style={s.field}>
                  <label style={s.label}>Address</label>
                  <input style={s.input} value={custForm.streetAddress1 || ''} onChange={e => updateCust('streetAddress1', e.target.value)} disabled={isReadOnly} />
                </div>
                <div style={s.fieldRow}>
                  <div style={s.field}>
                    <label style={s.label}>Zip Code</label>
                    <input style={s.input} value={custForm.zipCode || ''} onChange={e => updateCust('zipCode', e.target.value)} disabled={isReadOnly} />
                  </div>
                  <div style={s.field}>
                    <label style={s.label}>City</label>
                    <input style={s.input} value={custForm.city || ''} onChange={e => updateCust('city', e.target.value)} disabled={isReadOnly} />
                  </div>
                </div>
                <div style={s.fieldRow}>
                  <div style={s.field}>
                    <label style={s.label}>Business Phone</label>
                    <input style={s.input} value={custForm.businessPhone || ''} onChange={e => updateCust('businessPhone', e.target.value)} disabled={isReadOnly} />
                  </div>
                  <div style={s.field}>
                    <label style={s.label}>Contact Phone</label>
                    <input style={s.input} value={custForm.homePhone || ''} onChange={e => updateCust('homePhone', e.target.value)} disabled={isReadOnly} />
                  </div>
                </div>
                <div style={s.field}>
                  <label style={s.label}>E-Mail</label>
                  <input style={s.input} value={custForm.emailAddress || ''} onChange={e => updateCust('emailAddress', e.target.value)} disabled={isReadOnly} />
                </div>
              </div>
              {/* Right panel */}
              <div style={s.panel}>
                <div style={s.field}>
                  <label style={s.label}>Related Name</label>
                  <input style={s.input} value={custForm.relatedName || ''} onChange={e => updateCust('relatedName', e.target.value)} disabled={isReadOnly} />
                </div>
                <div style={s.field}>
                  <label style={s.label}>PO Box</label>
                  <input style={s.input} value={custForm.poBox || ''} onChange={e => updateCust('poBox', e.target.value)} disabled={isReadOnly} />
                </div>
                <div style={s.field}>
                  <label style={s.label}>State</label>
                  <select style={s.input} value={custForm.state || ''} onChange={e => updateCust('state', e.target.value)} disabled={isReadOnly}>
                    <option value="">--</option>
                    {US_STATES.map(st => <option key={st} value={st}>{st}</option>)}
                  </select>
                </div>
                <div style={s.field}>
                  <label style={s.label}>Cell Phone</label>
                  <input style={s.input} value={custForm.cellPhone || ''} onChange={e => updateCust('cellPhone', e.target.value)} disabled={isReadOnly} />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Vehicle Section */}
        {showVehicle && vehForm && (
          <div style={s.section}>
            <div style={s.sectionTitle}>Vehicle</div>
            <div style={s.twoPanel}>
              {/* Left panel */}
              <div style={s.panel}>
                <div style={s.field}>
                  <label style={s.label}>Registered Owner</label>
                  <input style={s.input} value={vehForm.registrantName || ''} onChange={e => updateVeh('registrantName', e.target.value)} disabled={isReadOnly} />
                </div>
                <div style={s.fieldRow}>
                  <div style={s.field}>
                    <label style={s.label}>Color</label>
                    <input style={s.input} value={vehForm.color || ''} onChange={e => updateVeh('color', e.target.value)} disabled={isReadOnly} />
                  </div>
                </div>
                <div style={s.field}>
                  <label style={s.label}>VIN</label>
                  <input style={s.input} value={vehForm.vin || ''} onChange={e => updateVeh('vin', e.target.value)} disabled={isReadOnly} />
                </div>
                <div style={s.fieldRow}>
                  <div style={s.field}>
                    <label style={s.label}>Year</label>
                    <input style={s.input} value={vehForm.vehicleYear || ''} onChange={e => updateVeh('vehicleYear', e.target.value)} disabled={isReadOnly} />
                  </div>
                  <div style={s.field}>
                    <label style={s.label}>Make</label>
                    <input style={s.input} value={vehForm.vehicleMake || ''} onChange={e => updateVeh('vehicleMake', e.target.value)} disabled={isReadOnly} />
                  </div>
                </div>
                <div style={s.field}>
                  <label style={s.label}>Model</label>
                  <input style={s.input} value={vehForm.vehicleModel || ''} onChange={e => updateVeh('vehicleModel', e.target.value)} disabled={isReadOnly} />
                </div>
              </div>
              {/* Right panel */}
              <div style={s.panel}>
                <div style={s.field}>
                  <label style={s.label}>Title Number</label>
                  <input style={s.input} value={vehForm.titleNumber || ''} onChange={e => updateVeh('titleNumber', e.target.value)} disabled={isReadOnly} />
                </div>
                <div style={s.field}>
                  <label style={s.label}>Fleet Number</label>
                  <input style={s.input} value={vehForm.fleetNumber || ''} onChange={e => updateVeh('fleetNumber', e.target.value)} disabled={isReadOnly} />
                </div>
                <div style={s.fieldRow}>
                  <div style={s.field}>
                    <label style={s.label}>Plate</label>
                    <input style={s.input} value={vehForm.licensePlate || ''} onChange={e => updateVeh('licensePlate', e.target.value)} disabled={isReadOnly} />
                  </div>
                  <div style={s.field}>
                    <label style={s.label}>State</label>
                    <select style={s.input} value={vehForm.licensePlateState || ''} onChange={e => updateVeh('licensePlateState', e.target.value)} disabled={isReadOnly}>
                      <option value="">--</option>
                      {US_STATES.map(st => <option key={st} value={st}>{st}</option>)}
                    </select>
                  </div>
                </div>
                <div style={s.field}>
                  <label style={s.label}>County</label>
                  <input style={s.input} value={vehForm.county || ''} onChange={e => updateVeh('county', e.target.value)} disabled={isReadOnly} />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div style={s.footer}>
        {!isReadOnly && (
          <button style={s.applyBtn} onClick={handleApply} disabled={saving}>
            {saving ? 'Saving...' : 'Apply'}
          </button>
        )}
        <button style={s.closeBtn} onClick={() => navigate(`/inspection/${pkInspection}`, { state: { pkInspection, historyState } })}>
          Close
        </button>
      </div>
    </div>
  );
}

const s = {
  page:       { minHeight: '100vh', display: 'flex', flexDirection: 'column', backgroundColor: '#f0f0f0', fontFamily: 'system-ui, sans-serif', fontSize: 13 },
  center:     { display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 40 },
  spinner:    { width: 36, height: 36, border: '4px solid #e0e0e0', borderTop: '4px solid #007AFF', borderRadius: '50%', animation: 'spin 0.8s linear infinite' },
  header:     { backgroundColor: '#1a1a2e', padding: '8px 12px', display: 'flex', alignItems: 'center', gap: 16 },
  headerTitle:{ color: '#fff', fontSize: 16, fontWeight: 700 },
  headerSub:  { color: 'rgba(255,255,255,0.6)', fontSize: 13 },
  error:      { padding: '8px 12px', backgroundColor: '#ffe0e0', color: '#cc0000', fontSize: 13 },
  body:       { flex: 1, padding: 8, display: 'flex', flexDirection: 'column', gap: 8, overflow: 'auto' },
  section:    { backgroundColor: '#fff', border: '1px solid #ddd', borderRadius: 4, padding: '8px 10px' },
  sectionTitle:{ fontWeight: 700, fontSize: 13, color: '#1a1a2e', marginBottom: 8, borderBottom: '1px solid #eee', paddingBottom: 4 },
  twoPanel:   { display: 'flex', gap: 16 },
  panel:      { flex: 1, display: 'flex', flexDirection: 'column', gap: 8 },
  fieldRow:   { display: 'flex', gap: 8 },
  field:      { flex: 1, display: 'flex', flexDirection: 'column', gap: 2 },
  label:      { fontSize: 12, color: '#666', fontWeight: 600 },
  input:      { padding: '4px 6px', border: '1px solid #ccc', borderRadius: 3, fontSize: 13, backgroundColor: '#fafafa', outline: 'none' },
  radioRow:   { display: 'flex', gap: 16, marginBottom: 8 },
  radioLabel: { display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, cursor: 'pointer' },
  footer:     { backgroundColor: '#e8e8e8', borderTop: '2px solid #bbb', padding: '6px 12px', display: 'flex', justifyContent: 'flex-end', gap: 8 },
  applyBtn:   { padding: '5px 20px', backgroundColor: '#007AFF', color: '#fff', border: 'none', borderRadius: 4, fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  closeBtn:   { padding: '5px 20px', backgroundColor: '#fff', color: '#333', border: '1px solid #bbb', borderRadius: 4, fontSize: 14, cursor: 'pointer' },
};